export const DEFAULT_PRODUCTION_METRIC_CODES = [
  "tonnes_milled",
  "ounces_produced",
  "aisc",
  "head_grade",
  "recovery",
  "strip_ratio",
];

export const PRODUCTION_METRIC_GROUPS = [
  {
    group: "Throughput & Mining Volumes",
    items: [
      { code: "tonnes_milled", label: "Tonnes milled" },
      { code: "throughput", label: "Throughput" },
      { code: "ore_mined", label: "Ore mined" },
      { code: "waste_mined", label: "Waste mined" },
      { code: "total_material_moved", label: "Total material moved" },
    ],
  },
  {
    group: "Production Quantities",
    items: [
      { code: "ounces_mined", label: "Ounces mined" },
      { code: "ounces_produced", label: "Ounces produced" },
      { code: "ounces_sold", label: "Ounces sold" },
      { code: "payable_metal", label: "Payable metal" },
    ],
  },
  {
    group: "Grades & Metallurgy",
    items: [
      { code: "head_grade", label: "Head grade" },
      { code: "recovery", label: "Recovery" },
      { code: "dilution", label: "Dilution" },
    ],
  },
  {
    group: "Mining Ratios & Efficiency",
    items: [
      { code: "strip_ratio", label: "Strip ratio" },
      { code: "utilization", label: "Utilization" },
      { code: "availability", label: "Availability" },
    ],
  },
  {
    group: "Cost Metrics",
    items: [
      { code: "aisc", label: "AISC" },
      { code: "cash_costs", label: "Cash costs" },
      { code: "operating_cost_per_tonne", label: "Operating cost per tonne" },
      { code: "mining_cost_per_tonne", label: "Mining cost per tonne" },
      { code: "processing_cost_per_tonne", label: "Processing cost per tonne" },
    ],
  },
  {
    group: "Financial Realizations",
    items: [{ code: "realized_price", label: "Realized price" }],
  },
];

export const CURRENCY_OPTIONS = [
  { value: "USD", label: "US Dollar (USD)" },
  { value: "CAD", label: "Canadian Dollar (CAD)" },
  { value: "AUD", label: "Australian Dollar (AUD)" },
  { value: "GBP", label: "British Pound (GBP)" },
  { value: "EUR", label: "Euro (EUR)" },
  { value: "SAR", label: "Saudi Riyal (SAR)" },
  { value: "SEK", label: "Swedish Krona (SEK)" },
  { value: "CNY", label: "Chinese Yuan (CNY)" },
  { value: "ZAR", label: "South African Rand (ZAR)" },
  { value: "PLN", label: "Polish Zloty (PLN)" },
  { value: "KRW", label: "South Korean Won (KRW)" },
  { value: "TRY", label: "Turkish Lira (TRY)" },
  { value: "JPY", label: "Japanese Yen (JPY)" },
  { value: "IDR", label: "Indonesian Rupiah (IDR)" },
];

export const formatProductionUnit = (unit, currency) => {
  if (!unit) return "";
  const resolvedCurrency = currency || "USD";
  const legacyCurrencyWithUnit = unit.match(/^([A-Z]{3})(\/.+)$/);
  if (legacyCurrencyWithUnit) {
    return `${resolvedCurrency}${legacyCurrencyWithUnit[2]}`;
  }
  if (/^[A-Z]{3}$/.test(unit)) {
    return resolvedCurrency;
  }
  if (unit === "money") return resolvedCurrency;
  if (unit === "/oz" || unit === "/t") return `${resolvedCurrency}${unit}`;
  return unit;
};

export const PRODUCTION_METRIC_CATEGORIES = PRODUCTION_METRIC_GROUPS.reduce((acc, group) => {
  group.items.forEach((item) => {
    acc[item.code] = group.group;
  });
  return acc;
}, {});

export const PRODUCTION_METRIC_ORDER = PRODUCTION_METRIC_GROUPS.reduce((acc, group, groupIndex) => {
  acc[group.group] = groupIndex;
  return acc;
}, {});

export const PRODUCTION_METRIC_CODE_ORDER = PRODUCTION_METRIC_GROUPS.reduce((acc, group) => {
  group.items.forEach((item, index) => {
    acc[item.code] = index;
  });
  return acc;
}, {});

const FINANCIAL_METRIC_GROUPS = new Set(["Cost Metrics", "Financial Realizations"]);

export const PRODUCTION_FINANCIAL_METRIC_CODES = PRODUCTION_METRIC_GROUPS.reduce(
  (acc, group) => {
    if (FINANCIAL_METRIC_GROUPS.has(group.group)) {
      group.items.forEach((item) => acc.add(item.code));
    }
    return acc;
  },
  new Set()
);

export const buildDefaultProductionMetricEntries = (
  productionMetrics,
  defaultCodes = DEFAULT_PRODUCTION_METRIC_CODES
) => {
  if (!Array.isArray(productionMetrics) || productionMetrics.length === 0) {
    return [];
  }
  const metricByCode = productionMetrics.reduce((acc, metric) => {
    acc[metric.code] = metric;
    return acc;
  }, {});
  return defaultCodes
    .map((code) => metricByCode[code])
    .filter(Boolean)
    .map((metric) => ({
      metric_id: metric.id,
      commodity_id: "",
      value: "",
    }));
};

export const buildProductionMetricEntriesFromValues = (values, productionMetrics) => {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  const metricIds = new Set((productionMetrics || []).map((metric) => metric.id));
  return values
    .map((val) => ({
      metric_id: val.metric?.id || val.metric,
      commodity_id: val.commodity?.id || val.commodity || "",
      value: val.value ?? "",
    }))
    .filter((entry) => entry.metric_id && metricIds.has(entry.metric_id));
};

export const buildProductionMetricOptions = (productionMetrics) =>
  (productionMetrics || [])
    .map((metric) => ({
      ...metric,
      category: PRODUCTION_METRIC_CATEGORIES[metric.code] || "Other",
    }))
    .sort((a, b) => {
      if (a.category === b.category) {
        const aOrder = PRODUCTION_METRIC_CODE_ORDER[a.code] ?? Number.MAX_SAFE_INTEGER;
        const bOrder = PRODUCTION_METRIC_CODE_ORDER[b.code] ?? Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return a.name.localeCompare(b.name);
      }
      const aGroupOrder = PRODUCTION_METRIC_ORDER[a.category] ?? Number.MAX_SAFE_INTEGER;
      const bGroupOrder = PRODUCTION_METRIC_ORDER[b.category] ?? Number.MAX_SAFE_INTEGER;
      return aGroupOrder - bGroupOrder;
    });
